import * as React from 'react';
import type {
  AutomationActionTriggerData
} from '../../../../MedicareProAPI/Entities/Automation/Automation';
import type {
  AUTOMATION_ACTION_TYPE,
  AutomationAction
} from '../../../../MedicareProAPI/Entities/Automation/AutomationAction';
import {
  AUTOMATION_ACTION_TYPE_ICONS,
  AUTOMATION_ACTION_TYPE_LABELS,
  AUTOMATION_TRIGGER_DATA_FIELD_TYPE_LABELS
} from '../../../Language/en';
import { IndFlagRenderer } from '../../IndFlagRenderer';
import { IndGenderRenderer } from '../../IndGenderRenderer';
import { IndPrefLangRenderer } from '../../IndPrefLangRenderer';

export interface AutomationActionCardProps {
  action: AutomationAction;
  nestedLevel?: number;
  onClick: (action?: AutomationAction) => void;
  onClickAddSubAction: (action?: AutomationAction) => void;
}

export const AutomationActionCard = (props: AutomationActionCardProps): React.JSX.Element => {
  const [action, setAction] = React.useState<AutomationAction>(null);
  const [nestedLevel] = React.useState<number>(props.nestedLevel || 0);

  const [actionType, setActionType] = React.useState<AUTOMATION_ACTION_TYPE>(null);
  const [actionId, setActionId] = React.useState<number>(null);

  const [actionData, setActionData] = React.useState({});
  const [actionTriggerCriteria, setActionTriggerCriteria] =
    React.useState<AutomationActionTriggerData[]>();
  const [actionStartDelayTime, setActionStartDelayTime] = React.useState(null);
  const [actionStartDelayDays, setActionStartDelayDays] = React.useState(null);

  const cndImageUrl = 'https://medicareprocrm-cdn.s3.us-east-2.amazonaws.com/public/img/site/';

  const providerVendorReferencesById = {
    2: 'zoom',
    1: 'ringcentral',
    4: 'goto',
    5: 'phonecom',
    6: 'dialpad',
    7: 'aweber',
    8: 'microsoft',
    10: 'google',
    11: 'thanksio',
    12: 'mailchimp',
    13: 'constantcontact',
  };

  const appVendorReferencesById = {
    1: 'simplii',
    2: 'intulse',
    3: 'convoso',
    4: 'talkdesk',
    5: 'mailboxpower',
    6: 'directmailmanager',
    17: 'medizues',
    7: 'currentclient',
    34: 'openphone',
    20: 'slybroadcast',
    39: 'textrequest',
    42: 'twilio'
  };

  const delayDaysOptions = [
    { value: '', label: 'Immediately' },
    { value: 0, label: 'Same Day' },
    { value: 1, label: '1 Day' },
    { value: 2, label: '2 Days' },
    { value: 3, label: '3 Days' },
    { value: 4, label: '4 Days' },
    { value: 5, label: '5 Days' },
    { value: 6, label: '6 Days' },
    { value: 7, label: '1 Week' },
    { value: 14, label: '2 Weeks' },
    { value: 21, label: '3 Weeks' },
    { value: 30, label: '1 Month' },
    { value: 60, label: '2 Months' },
    { value: 90, label: '3 Months' },
    { value: 120, label: '4 Months' },
    { value: 150, label: '5 Months' },
    { value: 180, label: '6 Months' },
    { value: 210, label: '7 Months' },
    { value: 240, label: '8 Months' },
    { value: 270, label: '9 Months' },
    { value: 300, label: '10 Months' },
    { value: 330, label: '11 Months' },
    { value: 360, label: '1 Year' },
  ];

  React.useEffect(() => {
    if (props.action) {
      handleActionChange(props.action);
    }
  }, [props.action]);

  const handleActionChange = (action: AutomationAction) => {
    setAction(action);
    setActionId(action.getId());
    setActionType(action.getType());
    setActionData(action.getData() || {});
    setActionTriggerCriteria(action.getTriggerCriteria());
    setActionStartDelayTime(action.getStartDelayTime());
    setActionStartDelayDays(action.getStartDelayDays());
  };

  const startTimeClass = ['badge', 'badge-pill', 'badge-success', 'p-2', 'px-3'];
  const formStartDelayDays = delayDaysOptions.find((item) =>
    actionStartDelayDays == null ? '' === item.value : actionStartDelayDays === item.value
  );
  const formStartDelayDaysLabel = formStartDelayDays ? formStartDelayDays.label : 'Immediately';

  if (formStartDelayDaysLabel == 'Immediately') {
    startTimeClass.push('bg-success');
  } else if (formStartDelayDaysLabel == 'Same Day') {
    startTimeClass.push('bg-teal');
  } else if (formStartDelayDaysLabel.indexOf('Day') >= 0) {
    startTimeClass.push('bg-cyan');
  } else if (formStartDelayDaysLabel.indexOf('Week') >= 0) {
    startTimeClass.push('bg-indigo');
  } else if (formStartDelayDaysLabel.indexOf('Month') >= 0) {
    startTimeClass.push('bg-salmon');
  }

  return (
    <div className={`nested-level-${nestedLevel}`}>
      <div
        className={`card p-3 mb-3 direction-s card-tall cp bg-contrast-hover`}
        onClick={(e) => {
          props.onClick(action);
        }}
      >
        <div className="row">
          <div className="col-12 mt-1 text-center">
            {actionTriggerCriteria && (
              <h6 className="mb-0">
                {actionTriggerCriteria.map((formTriggerCriteriaSingle: any) => {
                  const criteriaValues =
                    formTriggerCriteriaSingle.allValues === true
                      ? null
                      : formTriggerCriteriaSingle.values &&
                        Array.isArray(formTriggerCriteriaSingle.values)
                        ? formTriggerCriteriaSingle.values
                        : [formTriggerCriteriaSingle.values];
                  const fieldData = AUTOMATION_TRIGGER_DATA_FIELD_TYPE_LABELS.find(
                    (label) => label.value === formTriggerCriteriaSingle.field
                  );

                  let Renderer;

                  if (formTriggerCriteriaSingle.field === 'userFlag') {
                    Renderer = IndFlagRenderer;
                  } else if (formTriggerCriteriaSingle.field === 'indPrefLang') {
                    Renderer = IndPrefLangRenderer;
                  } else if (formTriggerCriteriaSingle.field === 'indGender') {
                    Renderer = IndGenderRenderer;
                  }

                  return (
                    <span
                      className="calMess badge badge-pill badge-multi-line border border-info p-2 px-3 mb-2 ml-2"
                      key={formTriggerCriteriaSingle.field}
                    >
                      <>{fieldData.label}: </>
                      {formTriggerCriteriaSingle.allValues === true && <>All Values</>}
                      {criteriaValues &&
                        criteriaValues.map((valueSingle, valueIndex) => {
                          const suffix = valueIndex + 1 !== criteriaValues.length ? ', ' : '';
                          return (
                            <React.Fragment key={valueIndex}>
                              {Renderer ? <Renderer value={valueSingle} /> : valueSingle}
                              {suffix}
                            </React.Fragment>
                          );
                        })}
                    </span>
                  );
                })}
              </h6>
            )}

            <h6 className="mb-0">
              {
                <span className={startTimeClass.join(' ')}>
                  <i className="far fa-clock mr-2"></i>
                  {formStartDelayDaysLabel} {actionStartDelayDays ? 'later' : ''}
                </span>
              }
              {actionType && (
                <span className="calMess badge badge-pill border border-info p-2 px-3 ml-2">
                  <i
                    className={
                      'mr-2 far ' +
                      AUTOMATION_ACTION_TYPE_ICONS.find((item) => actionType === item.value).label
                    }
                  ></i>
                  {AUTOMATION_ACTION_TYPE_LABELS.find((item) => actionType === item.value).label}
                </span>
              )}
              {actionData['appId'] && (
                <>
                  <i className="fal fa-long-arrow-right ml-2 mr-2 calMess"></i>
                  <img
                    className=" pt-0 mt-0 automation-action-vendor-badge"
                    alt="{}"
                    src={cndImageUrl + appVendorReferencesById[actionData['appId']] + 'icon.png'}
                  ></img>
                </>
              )}
              {actionData['userIntegrationProviderId'] && (
                <>
                  <i className="fal fa-long-arrow-right ml-2 mr-2 calMess"></i>
                  <img
                    className=" pt-0 mt-0 automation-action-vendor-badge"
                    alt="{}"
                    src={
                      cndImageUrl +
                      providerVendorReferencesById[actionData['userIntegrationProviderId']] +
                      'icon.png'
                    }
                  ></img>
                </>
              )}
              {!actionData['userIntegrationProviderId'] &&
                !actionData['appId'] &&
                actionType == 'send_email' && (
                  <>
                    <i className="fal fa-long-arrow-right ml-2 mr-2 calMess"></i>
                    <img
                      className=" pt-0 mt-0 automation-action-vendor-badge"
                      alt="{}"
                      src={cndImageUrl + 'mpcrmicon.png'}
                    ></img>
                  </>
                )}
            </h6>
          </div>
        </div>
      </div>
      <button
        className="btn btn-sm btn-secondary"
        style={{ border: 'none', borderRadius: '0 0 5px 5px', marginTop: '-2.1rem' }}
        type="button"
        onClick={(e) => props.onClickAddSubAction(action)}
      >
        <i className="far fa-plus-circle mr-1" /> Add Sub-Action
      </button>
    </div>
  );
};
